import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import classNames from 'classnames';

import {
  ATLASSIAN_PROFILE_URL,
  TRELLO_AA_HELP_URL,
} from '@trello/aa-migration';
import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { isTouch } from '@trello/browser';
import { forNamespace } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Spinner } from '@trello/nachos/spinner';

import { ExternalLink } from './ExternalLink';
import { ListItem } from './ListItem';

// eslint-disable-next-line @trello/less-matches-component
import styles from './AtlassianAccountMigrationConfirmationDialog.less';

const format = forNamespace(['aa migration confirmation'], {
  shouldEscapeStrings: false,
});

interface AaEnterpriseMigrationContentProps {
  enterpriseName: string;
  isLoading: boolean;
  isSsoEnforced: boolean;
  onDismiss: () => void;
  analyticsSource: SourceType;
  analyticsContext: Parameters<typeof Analytics.sendUIEvent>[0]['attributes'];
}

export const AaEnterpriseMigrationContent: FunctionComponent<
  AaEnterpriseMigrationContentProps
> = ({
  enterpriseName,
  isSsoEnforced,
  isLoading,
  onDismiss,
  analyticsSource,
  analyticsContext,
}) => {
  const onClickGotItButton = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissButton',
      source: analyticsSource,
      attributes: analyticsContext,
    });
    onDismiss();
  }, [analyticsContext, analyticsSource, onDismiss]);

  return (
    <div className={styles.description}>
      <ListItem>
        {isSsoEnforced
          ? format('enterprise-login-message-sso')
          : format('enterprise-login-message-atl')}
      </ListItem>
      <ListItem>
        {format('enterprise-managed-account-message', {
          enterpriseName,
        })}
      </ListItem>
      <ListItem>
        {format('review-profile-message', {
          reviewProfileLink: (
            <ExternalLink
              href={ATLASSIAN_PROFILE_URL}
              key="review-profile-link"
              linkName={'manageProfileLink'}
              analyticsContext={analyticsContext}
              analyticsSource={analyticsSource}
            >
              {format('review-profile-link')}
            </ExternalLink>
          ),
        })}
      </ListItem>
      {isTouch() && <ListItem>{format('mobile-login-message')}</ListItem>}
      <div className={styles.buttonSection}>
        <Button
          appearance="primary"
          size="wide"
          onClick={onClickGotItButton}
          isDisabled={isLoading}
        >
          {format('got it')}
        </Button>
        <ExternalLink
          href={TRELLO_AA_HELP_URL}
          linkName={'learnMoreLink'}
          analyticsContext={analyticsContext}
          analyticsSource={analyticsSource}
        >
          {format('learn more')}
        </ExternalLink>
        {isLoading && (
          <Spinner
            small
            inline={true}
            wrapperClassName={classNames(styles.spinner)}
          />
        )}
      </div>
    </div>
  );
};
